import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { RootState } from '../store';
import { setFilters, setResults, setKeyword } from '../store/slices/searchSlice';
import MovieCard from '../components/movie/MovieCard';
import styles from './Search.module.css';

// Import JSON data
import dramaData from '../data/drama-full-1-short-dramas.json';

import { trackPageView } from '../utils/page-tracker';

const Search: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { keyword, filters, results } = useSelector((state: RootState) => state.search);
  const [_selectedCategory, setSelectedCategory] = useState<string>('');
  const [_selectedGenre, setSelectedGenre] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState<string>('');
  const [_isFromGenres, setIsFromGenres] = useState<boolean>(false);
  
  // 添加返回顶部功能
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  // 监听滚动事件
  useEffect(() => {
    trackPageView();
    const handleScroll = () => {
      // 当页面滚动超过300px时显示返回顶部按钮
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // 返回顶部函数
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Get unique years from data
  const years = Array.from(
    new Set([
      ...dramaData.data.map(item => item.year.toString())
    ])
  ).sort((a, b) => Number(b) - Number(a));

  useEffect(() => {
    // Get filters from URL
    const categoryParam = searchParams.get('category');
    const genreParam = searchParams.get('genre');
    const yearParam = searchParams.get('year');
    const keywordParam = searchParams.get('keyword');
    const sortParam = searchParams.get('sort');
    const typeParam = searchParams.get('type');
    
    // 判断是否从首页底部的genres点进来的
    if (genreParam) {
      setIsFromGenres(true);
      setSelectedGenre(genreParam);
      dispatch(setFilters({ genre: genreParam }));
    } else {
      setIsFromGenres(false);
    }
    
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      dispatch(setFilters({ category: categoryParam }));
    } else if (typeParam === 'short-videos') {
      // 处理旧的type参数，将其转换为Short Videos分类
      setSelectedCategory('Short Videos');
      dispatch(setFilters({ category: 'Short Videos' }));
    }
    
    if (yearParam) {
      setSelectedYear(yearParam);
      dispatch(setFilters({ year: yearParam }));
    }
    
    if (keywordParam) {
      dispatch(setKeyword(keywordParam));
    }
    
    if (sortParam) {
      setSelectedSort(sortParam);
    }
  }, [searchParams, dispatch]);
  
  useEffect(() => {
    // Perform search
    let filteredResults = [...dramaData.data];
    
    // Filter by keyword
    if (keyword) {
      filteredResults = filteredResults.filter(item => 
        item.title.toLowerCase().includes(keyword.toLowerCase())
      );
    }
    
    // 按genre参数筛选
    if (filters.genre) {
      filteredResults = filteredResults.filter(item => 
        item.genres && item.genres.some(g => 
          g.toLowerCase().includes(filters.genre!.toLowerCase()) || 
          filters.genre!.toLowerCase().includes(g.toLowerCase())
        )
      );
    }
    // 否则按常规分类处理
    else if (filters.category) {
      // 特殊处理"Trending Now"分类，使用首页的Trending Now数据
      if (filters.category === 'Trending Now') {
        // 使用slice(5, 15)与首页保持一致
        filteredResults = [...dramaData.data.slice(5, 15)];
      } 
      // 移除Short Videos特殊处理
      else {
        filteredResults = filteredResults.filter(item => 
          item.categories.includes(filters.category as string)
        );
      }
    }
    
    // Filter by year
    if (filters.year) {
      filteredResults = filteredResults.filter(item => 
        item.year.toString() === filters.year
      );
    }
    
    // 创建一个新的数组副本用于排序
    let sortedResults = [...filteredResults];
    
    // Sort results
    if (selectedSort === 'latest') {
      sortedResults = [...sortedResults].sort((a, b) => Number(b.year) - Number(a.year));
    } else if (selectedSort === 'trending') {
      // 使用首页的Trending Now数据，与分类保持一致
      sortedResults = [...dramaData.data.slice(5, 15)];
    } else if (selectedSort === 'score') {
      sortedResults = [...sortedResults].sort((a, b) => Number(b.score) - Number(a.score));
    }
    
    dispatch(setResults(sortedResults));
  }, [keyword, filters, selectedSort, dispatch]);
  
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    dispatch(setFilters({ year }));
    
    const newParams = new URLSearchParams(searchParams);
    if (year) {
      newParams.set('year', year);
    } else {
      newParams.delete('year');
    }
    setSearchParams(newParams);
  };
  
  const handleSortChange = (sort: string) => {
    setSelectedSort(sort);
    
    const newParams = new URLSearchParams(searchParams);
    if (sort) {
      newParams.set('sort', sort);
    } else {
      newParams.delete('sort');
    }
    setSearchParams(newParams);
  };
  
  return (
    <div className={styles.search}>
      <div className={styles.searchHeader}>
        <h1 className={styles.searchTitle}>
          {keyword ? `Search results for "${keyword}"` : 
           filters.genre ? `Genre: ${filters.genre}` :
           filters.category ? `${filters.category}` :
           filters.year ? `Year: ${filters.year}` :
           'All Content'}
        </h1>
        <div className={styles.resultCount}>{results.length} results</div>
      </div>
      
      <div className={styles.searchContainer}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <h3 className={styles.filterTitle}>Year</h3>
            <div className={styles.filterOptions}>
              <button 
                className={`${styles.filterOption} ${!selectedYear ? styles.active : ''}`}
                onClick={() => handleYearChange('')}
              >
                All
              </button>
              {years.map(year => (
                <button 
                  key={year}
                  className={`${styles.filterOption} ${selectedYear === year ? styles.active : ''}`}
                  onClick={() => handleYearChange(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
          
          <div className={styles.filterGroup}>
            <h3 className={styles.filterTitle}>Sort By</h3>
            <div className={styles.filterOptions}>
              <button 
                className={`${styles.filterOption} ${!selectedSort ? styles.active : ''}`}
                onClick={() => handleSortChange('')}
              >
                Default
              </button>
              <button 
                className={`${styles.filterOption} ${selectedSort === 'latest' ? styles.active : ''}`}
                onClick={() => handleSortChange('latest')}
              >
                Latest
              </button>
              <button 
                className={`${styles.filterOption} ${selectedSort === 'trending' ? styles.active : ''}`}
                onClick={() => handleSortChange('trending')}
              >
                Trending
              </button>
              <button 
                className={`${styles.filterOption} ${selectedSort === 'score' ? styles.active : ''}`}
                onClick={() => handleSortChange('score')}
              >
                Highest Rated
              </button>
            </div>
          </div>
        </div>
        
        <div className={styles.results}>
          {results.length > 0 ? (
            <motion.div 
              className={styles.resultsGrid}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {results.map((movie, index) => (
                <MovieCard key={movie._id} movie={movie} index={index} />
              ))}
            </motion.div>
          ) : (
            <div className={styles.noResults}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 3C6.13401 3 3 6.13401 3 10C3 13.866 6.13401 17 10 17C11.5723 17 13.0236 16.4816 14.2 15.6M22 22L18 18M15 10C15 12.7614 12.7614 15 10 15C7.23858 15 5 12.7614 5 10C5 7.23858 7.23858 5 10 5C12.7614 5 15 7.23858 15 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3>No results found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
      
      {/* 返回顶部按钮 */}
      {showBackToTop && (
        <button 
          className={styles.backToTop} 
          onClick={scrollToTop}
          aria-label="返回顶部"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5 12L12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>
  );
};

export default Search; 